import React, { useState, useEffect, useMemo } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';
import {
    SECTION_A_AREAS,
    SECTION_B_ITEMS,
    COMPETENCY_LEGEND,
    getCompetencyBand,
    LIKERT_OPTIONS
} from '../../constants/peopleCareConstants';

/**
 * 📊 PeopleCareAnalyticsView — Dashboard di Misurazione Statistica Aggregata People Care (Buenavista)
 * 
 * Permette all'Admin di vedere TUTTI i dati aggregati, calcolare frequenze e percentuali
 * per ogni tipologia di risposta, confrontare IN vs OUT (Delta) e monitorare le competenze.
 */
const PeopleCareAnalyticsView = ({ user, counselorsList = [], onBack, onOpenClient }) => {
    const [clients, setClients] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [counselors, setCounselors] = useState(counselorsList || []);
    const [selectedOrg, setSelectedOrg] = useState(user.organizationId || 'buenavista');
    const [loading, setLoading] = useState(true);
    const [selectedCounselor, setSelectedCounselor] = useState('all');
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'participants' | 'sectionA' | 'sectionB' | 'sectionC' | 'competencies'
    const [viewMode, setViewMode] = useState('sequence'); // 'sequence' (una sezione in seguito all'altra) | 'tabs'
    const [participantSearch, setParticipantSearch] = useState('');
    const [participantStatusFilter, setParticipantStatusFilter] = useState('all'); // 'all' | 'in_done' | 'out_done' | 'pending'
    const [expandedParticipantId, setExpandedParticipantId] = useState(null);

    const scrollToSection = (sectionId, targetTab) => {
        const tabMap = {
            'sec-participants': 'participants',
            'sec-sectionA': 'sectionA',
            'sec-sectionB': 'sectionB',
            'sec-sectionC': 'sectionC',
            'sec-competencies': 'competencies'
        };
        const tab = targetTab || tabMap[sectionId] || 'participants';

        if (viewMode === 'tabs') {
            setActiveTab(tab);
        } else {
            const el = document.getElementById(sectionId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const isAdmin = user.isAdmin || user.email?.toLowerCase() === 'nuvola@awarenesstool.it' || user.email?.toLowerCase() === 'marilisa.delve@gmail.com';
    const isOrgAdmin = user.isOrgAdmin || user.role === 'org_admin' || user.email?.toLowerCase() === 'patrizia.marforio@gmail.com' || user.email?.toLowerCase() === 'marzia.macca@gmail.com' || user.email?.toLowerCase() === 'trebino.martina@gmail.com' || user.email?.toLowerCase() === 'patrizia.marchi.1969@gmail.com';
    const hasBuenaVistaAccess = Boolean(
        isAdmin || 
        isOrgAdmin || 
        user.organizationId === 'buenavista' || 
        (Array.isArray(user.allowedOrganizations) && user.allowedOrganizations.includes('buenavista')) ||
        (user.organizationName || '').toLowerCase().includes('buenavista')
    );
    const canFilterAllOrgs = isAdmin && !isOrgAdmin;

    useEffect(() => {
        if (counselorsList && counselorsList.length > 0) {
            setCounselors(counselorsList);
        }
    }, [counselorsList]);

    useEffect(() => {
        if (hasBuenaVistaAccess) {
            loadData();
        } else {
            setLoading(false);
        }
    }, [selectedCounselor, selectedOrg, hasBuenaVistaAccess]);

    const loadData = async () => {
        if (!hasBuenaVistaAccess) return;
        try {
            setLoading(true);
            const [allClients, allAssessments, allOrgs, allMeetings, fetchedCounselors] = await Promise.all([
                DataManager.getClients((isAdmin || isOrgAdmin) && selectedCounselor !== 'all' ? selectedCounselor : null),
                DataManager.getAllAssessments(),
                DataManager.getAllOrganizations(),
                DataManager.getAllMeetings ? DataManager.getAllMeetings() : Promise.resolve([]),
                (counselorsList.length === 0 && DataManager.getAllCounselors) ? DataManager.getAllCounselors() : Promise.resolve([])
            ]);

            setOrganizations(allOrgs || []);
            setMeetings(allMeetings || []);
            if (fetchedCounselors && fetchedCounselors.length > 0) {
                setCounselors(fetchedCounselors);
            } else if (counselorsList && counselorsList.length > 0) {
                setCounselors(counselorsList);
            }

            // Filter for organizational clients
            const orgClients = (allClients || []).filter(c => {
                const isOrg = c.settingType === 'organizzativo' || c.organization || c.organizationName;
                if (!isOrg) return false;
                if (selectedOrg === 'all') return true;
                const clientOrgId = (c.organization || '').toLowerCase();
                const clientOrgName = (c.organizationName || '').toLowerCase();
                return clientOrgId === selectedOrg.toLowerCase() || clientOrgName.includes(selectedOrg.toLowerCase());
            });
            setClients(orgClients);

            // Filter People Care assessments
            const pcAssessments = (allAssessments || []).filter(a => {
                const isPCAssessment = a.type === 'people_care_in' || a.type === 'people_care_out' || a.organization;
                if (!isPCAssessment) return false;
                if (selectedOrg === 'all') return true;
                const assOrg = (a.organization || '').toLowerCase();
                return assOrg === selectedOrg.toLowerCase() || (selectedOrg === 'buenavista' && (!assOrg || assOrg === 'buenavista'));
            });
            setAssessments(pcAssessments);

        } catch (err) {
            console.error("Errore caricamento dati Counseling Organizzativo Analytics:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- COMPUTED STATS ---

    // Assessments IN & OUT
    const inAssessments = useMemo(() => assessments.filter(a => a.type === 'people_care_in' || a.phase === 'IN'), [assessments]);
    const outAssessments = useMemo(() => assessments.filter(a => a.type === 'people_care_out' || a.phase === 'OUT'), [assessments]);

    // Paired Clients (who have both IN and OUT)
    const pairedClients = useMemo(() => {
        const inClientIds = new Set(inAssessments.map(a => a.clientId));
        const outClientIds = new Set(outAssessments.map(a => a.clientId));
        return Array.from(inClientIds).filter(id => outClientIds.has(id));
    }, [inAssessments, outAssessments]);

    // Section A Stats (Count and % of clients for each need area)
    const sectionAStats = useMemo(() => {
        const totalIn = inAssessments.length;
        const counts = {};
        SECTION_A_AREAS.forEach(area => { counts[area.id] = 0; });
        let otherCount = 0;
        const otherExamples = [];

        inAssessments.forEach(ass => {
            const areas = ass.sectionA?.selectedAreas || [];
            areas.forEach(areaId => {
                if (counts[areaId] !== undefined) {
                    counts[areaId] += 1;
                }
            });
            if (ass.sectionA?.otherAreaText) {
                otherCount += 1;
                otherExamples.push({
                    client: ass.clientName,
                    text: ass.sectionA.otherAreaText
                });
            }
        });

        const items = SECTION_A_AREAS.map(area => ({
            ...area,
            count: counts[area.id] || 0,
            percentage: totalIn > 0 ? ((counts[area.id] / totalIn) * 100).toFixed(1) : 0
        })).sort((a, b) => b.count - a.count);

        return {
            totalIn,
            items,
            otherCount,
            otherExamples
        };
    }, [inAssessments]);

    // Section B Stats (Evolution IN vs OUT for all 10 items)
    const sectionBStats = useMemo(() => {
        const results = SECTION_B_ITEMS.map(item => {
            // Calculate Average IN
            const inScores = inAssessments
                .map(a => a.sectionB?.[item.id])
                .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
                .map(Number);
            const avgIn = inScores.length > 0 
                ? (inScores.reduce((a, b) => a + b, 0) / inScores.length).toFixed(2) 
                : null;

            // Calculate Average OUT
            const outScores = outAssessments
                .map(a => a.sectionB?.[item.id])
                .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
                .map(Number);
            const avgOut = outScores.length > 0 
                ? (outScores.reduce((a, b) => a + b, 0) / outScores.length).toFixed(2) 
                : null;

            let delta = null;
            let percentChange = null;
            if (avgIn && avgOut) {
                delta = (Number(avgOut) - Number(avgIn)).toFixed(2);
                percentChange = Number(avgIn) > 0 ? (((Number(avgOut) - Number(avgIn)) / Number(avgIn)) * 100).toFixed(1) : null;
            }

            return {
                ...item,
                avgIn: avgIn ? Number(avgIn) : null,
                avgOut: avgOut ? Number(avgOut) : null,
                delta: delta ? Number(delta) : null,
                percentChange: percentChange ? Number(percentChange) : null,
                inCount: inScores.length,
                outCount: outScores.length
            };
        });

        // Global Averages across all 10 items
        const validInAvgs = results.map(r => r.avgIn).filter(Boolean);
        const validOutAvgs = results.map(r => r.avgOut).filter(Boolean);
        const globalAvgIn = validInAvgs.length > 0 
            ? (validInAvgs.reduce((a, b) => a + b, 0) / validInAvgs.length).toFixed(2) 
            : null;
        const globalAvgOut = validOutAvgs.length > 0 
            ? (validOutAvgs.reduce((a, b) => a + b, 0) / validOutAvgs.length).toFixed(2) 
            : null;

        return {
            items: results,
            globalAvgIn: globalAvgIn ? Number(globalAvgIn) : null,
            globalAvgOut: globalAvgOut ? Number(globalAvgOut) : null,
            globalDelta: (globalAvgIn && globalAvgOut) ? (Number(globalAvgOut) - Number(globalAvgIn)).toFixed(2) : null
        };
    }, [inAssessments, outAssessments]);

    // Section C Stats (Outcomes & NPS)
    const sectionCStats = useMemo(() => {
        const goalScores = outAssessments
            .map(a => a.sectionC?.goalAchievement)
            .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
            .map(Number);
        const avgGoal = goalScores.length > 0 
            ? (goalScores.reduce((a, b) => a + b, 0) / goalScores.length).toFixed(2) 
            : null;

        const npsScores = outAssessments
            .map(a => a.sectionC?.npsRecommendation)
            .filter(v => v !== null && v !== undefined && !isNaN(Number(v)))
            .map(Number);
        const avgNps = npsScores.length > 0 
            ? (npsScores.reduce((a, b) => a + b, 0) / npsScores.length).toFixed(2) 
            : null;

        // NPS distribution (quanti 5, quanti 4, quanti 3...)
        const npsDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        npsScores.forEach(s => { if (npsDistribution[s] !== undefined) npsDistribution[s] += 1; });

        // Goal distribution
        const goalDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        goalScores.forEach(s => { if (goalDistribution[s] !== undefined) goalDistribution[s] += 1; });

        // Qualitative Testimonials
        const testimonials = outAssessments.map(a => ({
            client: a.clientName,
            manager: a.managerName,
            whatChanged: a.sectionC?.whatChanged,
            mostUseful: a.sectionC?.mostUseful,
            differentApproach: a.sectionC?.differentApproach
        })).filter(t => t.whatChanged || t.mostUseful || t.differentApproach);

        return {
            totalOut: outAssessments.length,
            avgGoal,
            avgNps,
            npsDistribution,
            goalDistribution,
            testimonials
        };
    }, [outAssessments]);

    // Competencies Distribution
    const competenciesStats = useMemo(() => {
        const bandsCount = {
            distintivo: 0,
            consolidata: 0,
            presidio: 0,
            consolidamento: 0,
            sviluppo: 0
        };

        const scores = [];
        clients.forEach(c => {
            if (c.competencyScore !== undefined && c.competencyScore !== null && c.competencyScore !== '') {
                const band = getCompetencyBand(c.competencyScore);
                if (band && bandsCount[band.id] !== undefined) {
                    bandsCount[band.id] += 1;
                    scores.push(Number(c.competencyScore));
                }
            }
        });

        const totalWithScore = scores.length;
        const avgScore = totalWithScore > 0 ? (scores.reduce((a, b) => a + b, 0) / totalWithScore).toFixed(2) : null;

        return {
            totalWithScore,
            avgScore,
            bands: COMPETENCY_LEGEND.map(b => ({
                ...b,
                count: bandsCount[b.id] || 0,
                percentage: totalWithScore > 0 ? (((bandsCount[b.id] || 0) / totalWithScore) * 100).toFixed(1) : 0
            }))
        };
    }, [clients]);

    // --- COMPUTED PARTICIPANTS DETAILS (CLIENTI, COUNSELOR, INCONTRI, OBIETTIVO) ---

    // Map meetings by clientId and clientEmail
    const clientMeetingsStats = useMemo(() => {
        const map = new Map();
        (meetings || []).forEach(m => {
            if (m.deletedAt) return;
            const keys = [];
            if (m.clientId) keys.push(String(m.clientId));
            if (m.clientEmail) keys.push(String(m.clientEmail).toLowerCase().trim());

            keys.forEach(key => {
                if (!map.has(key)) map.set(key, []);
                const list = map.get(key);
                if (!list.some(existing => existing.id === m.id)) {
                    list.push(m);
                }
            });
        });
        return map;
    }, [meetings]);

    const getClientMeetingsList = (client) => {
        const idKey = client.id ? String(client.id) : null;
        const emailKey = client.email ? String(client.email).toLowerCase().trim() : null;

        const byId = idKey && clientMeetingsStats.has(idKey) ? clientMeetingsStats.get(idKey) : [];
        const byEmail = emailKey && clientMeetingsStats.has(emailKey) ? clientMeetingsStats.get(emailKey) : [];

        const combinedMap = new Map();
        byId.forEach(m => combinedMap.set(m.id, m));
        byEmail.forEach(m => combinedMap.set(m.id, m));

        let list = Array.from(combinedMap.values());
        if (list.length === 0 && Array.isArray(client.meetings) && client.meetings.length > 0) {
            list = client.meetings.filter(m => !m.deletedAt);
        }

        return list.sort((a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0));
    };

    const getClientMeetingCount = (client) => {
        const list = getClientMeetingsList(client);
        if (list.length > 0) return list.length;
        if (client.meetingsCount && !isNaN(Number(client.meetingsCount))) {
            return Number(client.meetingsCount);
        }
        return 0;
    };

    const getClientCounselorName = (client) => {
        const cId = client.counselorId || client.assignedCounselorId;
        const cEmail = (client.counselorEmail || '').toLowerCase().trim();

        if (cEmail === 'martina@gmail.com' || (cId && typeof cId === 'string' && (cId.toLowerCase().includes('vaccarone') || cId.toLowerCase().includes('tavarone')))) return 'Martina Vaccarone';
        if (cEmail === 'trebino.martina@gmail.com' || (cId && typeof cId === 'string' && cId.toLowerCase().includes('trebino'))) return 'Martina Trebino';

        const found = (counselors || []).find(c => 
            (cId && (c.id === cId || c.uid === cId || c.firebaseUid === cId)) ||
            (cEmail && c.email && c.email.toLowerCase().trim() === cEmail) ||
            (cId && c.email && c.email.toLowerCase().trim() === cId.toLowerCase().trim())
        );

        if (found?.email?.toLowerCase().trim() === 'martina@gmail.com') return 'Martina Vaccarone';
        if (found?.email?.toLowerCase().trim() === 'trebino.martina@gmail.com') return 'Martina Trebino';

        if (found?.name) return found.name;
        if (client.counselorName && client.counselorName.trim()) {
            if (cEmail === 'martina@gmail.com') return 'Martina Vaccarone';
            return client.counselorName.trim();
        }
        if (found?.email) return found.email;
        if (client.counselorEmail && client.counselorEmail.trim()) return client.counselorEmail.trim();

        const inAss = inAssessments.find(a => a.clientId === client.id || (client.email && a.clientEmail === client.email));
        if (inAss?.counselorName && inAss.counselorName.trim()) return inAss.counselorName.trim();

        const outAss = outAssessments.find(a => a.clientId === client.id || (client.email && a.clientEmail === client.email));
        if (outAss?.counselorName && outAss.counselorName.trim()) return outAss.counselorName.trim();

        return cId ? 'Counselor Assegnato' : 'Non assegnato';
    };

    const getClientGoalInfo = (client) => {
        const inAss = inAssessments.find(a => a.clientId === client.id || (client.email && a.clientEmail === client.email));
        const outAss = outAssessments.find(a => a.clientId === client.id || (client.email && a.clientEmail === client.email));

        // 1. Initial qualitative goal from questionnaire IN
        const initialGoal = inAss?.sectionA?.initialGoalText || client.initialGoal || client.goal || client.objective || '';

        // 2. Selected areas of need from questionnaire IN
        const selectedAreaIds = inAss?.sectionA?.selectedAreas || [];
        const areas = selectedAreaIds.map(areaId => {
            const def = SECTION_A_AREAS.find(a => a.id === areaId);
            return def ? { id: areaId, label: def.label, icon: def.icon } : { id: areaId, label: areaId, icon: '🎯' };
        });
        const otherArea = inAss?.sectionA?.otherAreaText || '';

        // 3. Contracts
        const contracts = DataManager.getClientContracts ? DataManager.getClientContracts(client) : (client.contracts || []);
        const activeContract = contracts.find(c => c.status === 'attivo') || contracts[0] || client.contract;
        const contractDescription = activeContract?.description && activeContract.description !== 'Definisci il tuo obiettivo qui...' 
            ? activeContract.description 
            : '';
        const contractTitle = activeContract?.title || '';

        // 4. Organizational need
        const organizationalNeed = client.organizationalNeed || '';

        // 5. Goal achievement rating from questionnaire OUT
        const goalAchievement = outAss?.sectionC?.goalAchievement || null;

        return {
            initialGoal,
            areas,
            otherArea,
            contractDescription,
            contractTitle,
            organizationalNeed,
            goalAchievement,
            hasIn: Boolean(inAss),
            hasOut: Boolean(outAss),
            inDate: inAss?.date,
            outDate: outAss?.date,
            inAssessment: inAss,
            outAssessment: outAss
        };
    };

    const enrichedParticipants = useMemo(() => {
        return clients.map(c => {
            const counselorName = getClientCounselorName(c);
            const meetingsList = getClientMeetingsList(c);
            const meetingCount = getClientMeetingCount(c);
            const goalInfo = getClientGoalInfo(c);

            return {
                ...c,
                counselorDisplayName: counselorName,
                meetingsList,
                meetingCount,
                goalInfo
            };
        });
    }, [clients, counselors, inAssessments, outAssessments, clientMeetingsStats]);

    const filteredParticipants = useMemo(() => {
        return enrichedParticipants.filter(p => {
            if (participantSearch.trim()) {
                const q = participantSearch.toLowerCase().trim();
                const name = (p.name || '').toLowerCase();
                const email = (p.email || '').toLowerCase();
                const counselor = (p.counselorDisplayName || '').toLowerCase();
                const goal = (p.goalInfo.initialGoal || '').toLowerCase();
                const orgNeed = (p.goalInfo.organizationalNeed || '').toLowerCase();
                const contract = (p.goalInfo.contractDescription || '').toLowerCase();
                const areas = p.goalInfo.areas.map(a => a.label.toLowerCase()).join(' ');

                const matches = name.includes(q) || email.includes(q) || counselor.includes(q) || 
                                goal.includes(q) || orgNeed.includes(q) || contract.includes(q) || areas.includes(q);
                if (!matches) return false;
            }

            if (participantStatusFilter === 'in_done') {
                return p.goalInfo.hasIn;
            }
            if (participantStatusFilter === 'out_done') {
                return p.goalInfo.hasOut;
            }
            if (participantStatusFilter === 'pending') {
                return !p.goalInfo.hasIn;
            }

            return true;
        });
    }, [enrichedParticipants, participantSearch, participantStatusFilter]);

    const totalMeetingsCount = useMemo(() => {
        return enrichedParticipants.reduce((sum, p) => sum + p.meetingCount, 0);
    }, [enrichedParticipants]);

    const exportParticipantsCSV = () => {
        const rows = [
            ["N", "PARTECIPANTE", "EMAIL", "COUNSELOR_ASSEGNATO", "NUMERO_INCONTRI", "OBIETTIVO_INIZIALE", "AREE_BISOGNO", "BISOGNO_ORGANIZZATIVO", "QUESTIONARIO_IN", "DATA_IN", "QUESTIONARIO_OUT", "DATA_OUT", "ESITO_OBIETTIVO"]
        ];

        enrichedParticipants.forEach((p, idx) => {
            const areasStr = p.goalInfo.areas.map(a => a.label).join("; ");
            rows.push([
                idx + 1,
                `"${p.name || ''}"`,
                `"${p.email || ''}"`,
                `"${p.counselorDisplayName || ''}"`,
                p.meetingCount,
                `"${(p.goalInfo.initialGoal || '').replace(/"/g, '""')}"`,
                `"${areasStr.replace(/"/g, '""')}"`,
                `"${(p.goalInfo.organizationalNeed || '').replace(/"/g, '""')}"`,
                p.goalInfo.hasIn ? "SI" : "NO",
                p.goalInfo.inDate || '',
                p.goalInfo.hasOut ? "SI" : "NO",
                p.goalInfo.outDate || '',
                p.goalInfo.goalAchievement ? `${p.goalInfo.goalAchievement}/5` : ''
            ]);
        });

        const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `partecipanti_counseling_${selectedOrg}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export to CSV
    const exportCSV = () => {
        const rows = [
            ["PARTECIPANTE", "FASE", "DATA", "RESPONSABILE", "MEDIA_SEZ_B", "GOAL_SEZ_C", "NPS_SEZ_C"]
        ];

        assessments.forEach(a => {
            rows.push([
                `"${a.clientName || a.clientId}"`,
                a.phase || (a.type === 'people_care_out' ? 'OUT' : 'IN'),
                a.date || '',
                `"${a.managerName || ''}"`,
                a.sectionB?.averageScore || '',
                a.sectionC?.goalAchievement || '',
                a.sectionC?.npsRecommendation || ''
            ]);
        });

        const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `people_care_buenavista_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!hasBuenaVistaAccess) {
        return (
            <div className="bg-white p-8 md:p-12 rounded-3xl text-center max-w-lg mx-auto shadow-sm border border-slate-200 my-12 animate-fade-in">
                <div className="w-16 h-16 mx-auto rounded-3xl bg-slate-100 flex items-center justify-center text-3xl mb-4">
                    🔒
                </div>
                <h3 className="text-xl font-black text-slate-800 tracking-tight mb-2">Accesso Riservato</h3>
                <p className="text-xs text-slate-500 leading-relaxed max-w-sm mx-auto mb-6">
                    Questa sezione è riservata all'organizzazione <strong>BuenaVista Cooperativa Sociale</strong>. Il tuo profilo counselor è configurato per attività individuale indipendente.
                </p>
                <button
                    onClick={onBack}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-xs font-bold transition shadow-sm cursor-pointer"
                >
                    ← Torna alla Dashboard
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            
            {/* Header Banner - High Contrast & Crisp Typography */}
            <div className="bg-slate-900 border border-slate-800 text-white rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                    <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-3 py-1 bg-indigo-500/20 border border-indigo-400/40 text-indigo-300 rounded-full text-xs font-black uppercase tracking-wider">
                                🏢 COUNSELING ORGANIZZATIVO
                            </span>
                            <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 rounded-full text-xs font-bold">
                                {selectedOrg === 'all' ? 'Tutte le Organizzazioni' : (organizations.find(o => o.id === selectedOrg)?.name || 'BuenaVista Cooperativa Sociale')}
                            </span>
                            {isOrgAdmin && (
                                <span className="px-2.5 py-0.5 bg-purple-500/20 border border-purple-400/40 text-purple-300 rounded-full text-[11px] font-bold">
                                    👤 Admin Ente: {user.name || user.email}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight drop-shadow">
                            Counseling Organizzativo — Dashboard di Misurazione & Analytics
                        </h1>
                        <p className="text-slate-200 text-xs md:text-sm mt-1.5 max-w-2xl font-medium">
                            Analisi quantitativa e qualitativa degli esiti del percorso: bisogni iniziali, confronto IN vs OUT su 10 dimensioni, NPS e matrice competenze.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={exportCSV}
                            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-2xl text-xs font-bold transition flex items-center gap-2"
                        >
                            <span>📥</span> Esporta CSV Dati
                        </button>
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-xs font-bold shadow-lg transition"
                            >
                                ← Torna alla Dashboard
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Controls Bar */}
                <div className="mt-6 pt-4 border-t border-slate-800 flex flex-wrap items-center gap-4">
                    {/* Organization / Azienda Selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-300 font-bold flex items-center gap-1">
                            <span>🏛️</span> Ente / Azienda:
                        </span>
                        {canFilterAllOrgs ? (
                            <select
                                value={selectedOrg}
                                onChange={e => setSelectedOrg(e.target.value)}
                                className="bg-slate-800 border border-slate-700 text-white text-xs font-bold rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                                <option value="all">🏢 Tutte le Aziende</option>
                                <option value="buenavista">BuenaVista Cooperativa Sociale</option>
                                {organizations.filter(o => o.id !== 'buenavista').map(o => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                            </select>
                        ) : (
                            <span className="px-3 py-1 bg-slate-800 border border-slate-700 text-indigo-300 text-xs font-bold rounded-xl">
                                {organizations.find(o => o.id === selectedOrg)?.name || 'BuenaVista Cooperativa Sociale'}
                            </span>
                        )}
                    </div>

                    {/* Counselor Filter */}
                    {(isAdmin || isOrgAdmin) && counselorsList.length > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-300 font-bold flex items-center gap-1">
                                <span>👥</span> Filtro Counselor:
                            </span>
                            <select
                                value={selectedCounselor}
                                onChange={e => setSelectedCounselor(e.target.value)}
                                className="bg-slate-800 border border-slate-700 text-white text-xs font-bold rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                                <option value="all">Tutti i Counselor</option>
                                {counselorsList.map(c => (
                                    <option key={c.id} value={c.id}>{c.name || c.email}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* KPI Summary Cards (Cliccabili per scorrere direttamente alla sezione desiderata) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* 1. PARTECIPANTI ENTE */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-participants')}
                    className="p-4 rounded-2xl border text-left transition-all cursor-pointer group relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white border-slate-200 shadow-sm hover:border-teal-400 hover:shadow-md hover:scale-[1.02]"
                    title="Clicca per visualizzare la tabella dei partecipanti con counselor, n° incontri e obiettivi"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                            Partecipanti Ente
                        </span>
                        <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 group-hover:bg-teal-600 group-hover:text-white transition">
                            Vedi 7 👥
                        </span>
                    </div>
                    <span className="text-3xl font-black text-slate-800 block my-0.5 group-hover:text-teal-700 transition">
                        {clients.length}
                    </span>
                    <span className="text-[11px] text-slate-500 flex items-center justify-between mt-1">
                        <span>Persone assegnate</span>
                        <span className="text-teal-600 font-bold group-hover:translate-x-0.5 transition-transform text-[11px]">Sez. 1 ↓</span>
                    </span>
                </button>

                {/* 2. QUESTIONARI IN */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-sectionA')}
                    className="p-4 rounded-2xl border text-left transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white border-slate-200 shadow-sm hover:border-teal-400 hover:shadow-md hover:scale-[1.02]"
                    title="Clicca per scorrere alla Sezione A: Bisogni Iniziali"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider block">Questionari IN</span>
                        <span className="text-[9px] font-bold text-teal-700">Sez. 2 ↓</span>
                    </div>
                    <span className="text-3xl font-black text-teal-700 block my-0.5">{inAssessments.length}</span>
                    <span className="text-[11px] text-slate-500 block">Avvii rilevati</span>
                </button>

                {/* 3. QUESTIONARI OUT */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-sectionC')}
                    className="p-4 rounded-2xl border text-left transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md hover:scale-[1.02]"
                    title="Clicca per scorrere alla Sezione C: Chiusure ed Esiti"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">Questionari OUT</span>
                        <span className="text-[9px] font-bold text-emerald-700">Sez. 4 ↓</span>
                    </div>
                    <span className="text-3xl font-black text-emerald-700 block my-0.5">{outAssessments.length}</span>
                    <span className="text-[11px] text-slate-500 block">Chiusure 8° incontro</span>
                </button>

                {/* 4. PERCORSI COMPLETI */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-sectionB')}
                    className="p-4 rounded-2xl border text-left transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white border-slate-200 shadow-sm hover:border-indigo-400 hover:shadow-md hover:scale-[1.02]"
                    title="Clicca per scorrere alla Sezione B: Delta IN vs OUT"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider block">Percorsi Completi</span>
                        <span className="text-[9px] font-bold text-indigo-700">Sez. 3 ↓</span>
                    </div>
                    <span className="text-3xl font-black text-indigo-700 block my-0.5">{pairedClients.length}</span>
                    <span className="text-[11px] text-slate-500 block">Confronto IN + OUT</span>
                </button>

                {/* 5. RAGGIUNGIMENTO OBIETTIVO */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-sectionC')}
                    className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-left hover:border-purple-300 hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
                    title="Clicca per scorrere agli esiti sugli obiettivi (Sez. 4)"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">Raggiung. Obiettivo</span>
                        <span className="text-[9px] font-bold text-purple-700">Sez. 4 ↓</span>
                    </div>
                    <span className="text-2xl font-black text-purple-700 block my-0.5">{sectionCStats.avgGoal ? `${sectionCStats.avgGoal} / 5` : 'N/D'}</span>
                    <span className="text-[11px] text-slate-500 block">Media esito finale</span>
                </button>

                {/* 6. RACCOMANDAZIONE NPS */}
                <button
                    type="button"
                    onClick={() => scrollToSection('sec-sectionC')}
                    className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-left hover:border-amber-300 hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400"
                    title="Clicca per scorrere al gradimento NPS (Sez. 4)"
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block">Raccomandazione NPS</span>
                        <span className="text-[9px] font-bold text-amber-700">Sez. 4 ↓</span>
                    </div>
                    <span className="text-2xl font-black text-amber-700 block my-0.5">{sectionCStats.avgNps ? `${sectionCStats.avgNps} / 5` : 'N/D'}</span>
                    <span className="text-[11px] text-slate-500 block">Gradimento servizio</span>
                </button>
            </div>

            {/* Navigation & Section Jump Bar (Sticky) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-2.5 rounded-2xl border border-slate-200 shadow-sm sticky top-2 z-20 backdrop-blur-md bg-white/95">
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 w-full sm:w-auto">
                    <button
                        onClick={() => {
                            setViewMode('sequence');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1.5 ${
                            viewMode === 'sequence' ? 'bg-teal-700 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        title="Visualizza tutte le sezioni una sotto l'altra in sequenza continua"
                    >
                        <span>📜</span> Tutte le Sezioni in Sequenza
                    </button>

                    <button
                        onClick={() => scrollToSection('sec-participants')}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                            viewMode === 'tabs' && activeTab === 'participants' ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        <span>👥</span> 1. Partecipanti ({clients.length})
                    </button>

                    <button
                        onClick={() => scrollToSection('sec-sectionA')}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                            viewMode === 'tabs' && activeTab === 'sectionA' ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        <span>🎯</span> 2. Sez. A: Bisogni ({sectionAStats.totalIn})
                    </button>

                    <button
                        onClick={() => scrollToSection('sec-sectionB')}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                            viewMode === 'tabs' && activeTab === 'sectionB' ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        <span>🔄</span> 3. Sez. B: Delta IN/OUT
                    </button>

                    <button
                        onClick={() => scrollToSection('sec-sectionC')}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                            viewMode === 'tabs' && activeTab === 'sectionC' ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        <span>🏆</span> 4. Sez. C: Valutazione
                    </button>

                    <button
                        onClick={() => scrollToSection('sec-competencies')}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1 ${
                            viewMode === 'tabs' && activeTab === 'competencies' ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        <span>⭐</span> 5. Competenze ({competenciesStats.totalWithScore})
                    </button>
                </div>

                {/* View Mode Toggle: Sequenza Continua vs Schede Separate */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl self-end sm:self-auto shrink-0">
                    <button
                        type="button"
                        onClick={() => setViewMode('sequence')}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition ${
                            viewMode === 'sequence' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                        }`}
                        title="Mostra tutte le sezioni una sotto l'altra"
                    >
                        📜 Flusso Continuo
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setViewMode('tabs');
                            if (activeTab === 'overview') setActiveTab('participants');
                        }}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition ${
                            viewMode === 'tabs' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                        }`}
                        title="Mostra una singola scheda per volta"
                    >
                        🗂️ Schede
                    </button>
                </div>
            </div>

            {/* Loading Spinner */}
            {loading ? (
                <div className="p-12 text-center text-slate-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-3"></div>
                    <p className="text-xs font-bold">Aggregazione e calcolo statistiche in corso...</p>
                </div>
            ) : (
                <>
                    {/* ========================================================================= */}
                    {/* SEZIONE 1: ELENCO PARTECIPANTI (CLIENTI, COUNSELOR, INCONTRI, OBIETTIVO) */}
                    {/* ========================================================================= */}
                    {(viewMode === 'sequence' || activeTab === 'participants' || activeTab === 'overview') && (
                        <section id="sec-participants" className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6 scroll-mt-24">
                            
                            {/* Header Section */}
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-slate-100">
                                <div>
                                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-[10px] font-black uppercase tracking-wider">
                                            👥 SEZIONE 1 — PARTECIPANTI ENTE
                                        </span>
                                        <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-[10px] font-bold">
                                            {selectedOrg === 'all' ? 'Tutte le Organizzazioni' : (organizations.find(o => o.id === selectedOrg)?.name || 'BuenaVista Cooperativa Sociale')}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                        I {clients.length} Partecipanti Assegnati al Percorso
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1 max-w-3xl font-medium">
                                        Riepilogo dedicato per ogni partecipante con nome del counselor di riferimento, numero di incontri/colloqui svolti e rispettivo obiettivo di percorso (da questionario iniziale IN o contratto).
                                    </p>
                                </div>

                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <button
                                        onClick={exportParticipantsCSV}
                                        className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-xs font-bold shadow-xs transition flex items-center gap-2"
                                        title="Esporta questa tabella in formato CSV"
                                    >
                                        <span>📥</span> Esporta Tabella CSV
                                    </button>
                                </div>
                            </div>

                            {/* Summary Stat Badges Bar */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Partecipanti Totali</span>
                                    <span className="text-2xl font-black text-slate-900 mt-0.5 block">{enrichedParticipants.length}</span>
                                    <span className="text-[11px] text-slate-500 font-medium">Persone in anagrafica</span>
                                </div>
                                <div className="p-4 bg-indigo-50/70 rounded-2xl border border-indigo-100">
                                    <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider block">Incontri Totali Svolti</span>
                                    <span className="text-2xl font-black text-indigo-900 mt-0.5 block">{totalMeetingsCount}</span>
                                    <span className="text-[11px] text-indigo-600 font-medium">Sedute registrate complessive</span>
                                </div>
                                <div className="p-4 bg-teal-50/70 rounded-2xl border border-teal-100">
                                    <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider block">Questionari IN Avviati</span>
                                    <span className="text-2xl font-black text-teal-900 mt-0.5 block">
                                        {enrichedParticipants.filter(p => p.goalInfo.hasIn).length}
                                    </span>
                                    <span className="text-[11px] text-teal-700 font-medium">Avvii 1° incontro</span>
                                </div>
                                <div className="p-4 bg-emerald-50/70 rounded-2xl border border-emerald-100">
                                    <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">Questionari OUT Chiusi</span>
                                    <span className="text-2xl font-black text-emerald-900 mt-0.5 block">
                                        {enrichedParticipants.filter(p => p.goalInfo.hasOut).length}
                                    </span>
                                    <span className="text-[11px] text-emerald-700 font-medium">Chiusure all'8° incontro</span>
                                </div>
                            </div>

                            {/* Search and Filters Bar */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                                <div className="relative flex-1 max-w-md">
                                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 text-sm">
                                        🔍
                                    </span>
                                    <input
                                        type="text"
                                        value={participantSearch}
                                        onChange={e => setParticipantSearch(e.target.value)}
                                        placeholder="Cerca per partecipante, counselor o obiettivo..."
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
                                    />
                                    {participantSearch && (
                                        <button 
                                            onClick={() => setParticipantSearch('')}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-xs text-slate-400 hover:text-slate-600"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                                    <button
                                        onClick={() => setParticipantStatusFilter('all')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                                            participantStatusFilter === 'all'
                                                ? 'bg-slate-800 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        Tutti ({enrichedParticipants.length})
                                    </button>
                                    <button
                                        onClick={() => setParticipantStatusFilter('in_done')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                                            participantStatusFilter === 'in_done'
                                                ? 'bg-teal-600 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        Con IN ({enrichedParticipants.filter(p => p.goalInfo.hasIn).length})
                                    </button>
                                    <button
                                        onClick={() => setParticipantStatusFilter('pending')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                                            participantStatusFilter === 'pending'
                                                ? 'bg-amber-600 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        In attesa IN ({enrichedParticipants.filter(p => !p.goalInfo.hasIn).length})
                                    </button>
                                    <button
                                        onClick={() => setParticipantStatusFilter('out_done')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                                            participantStatusFilter === 'out_done'
                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        Con OUT ({enrichedParticipants.filter(p => p.goalInfo.hasOut).length})
                                    </button>
                                </div>
                            </div>

                            {/* Main Participants Table */}
                            <div className="overflow-x-auto border border-slate-200 rounded-3xl shadow-xs">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50/90 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            <th className="p-4 text-center w-12">#</th>
                                            <th className="p-4 min-w-[200px]">Partecipante</th>
                                            <th className="p-4 min-w-[170px]">Counselor Assegnato</th>
                                            <th className="p-4 min-w-[160px]">N° Incontri</th>
                                            <th className="p-4 min-w-[320px]">Obiettivo di Percorso</th>
                                            <th className="p-4 min-w-[140px] text-center">Stato Questionari</th>
                                            {onOpenClient && (
                                                <th className="p-4 text-right min-w-[90px]">Azioni</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-xs">
                                        {filteredParticipants.length === 0 ? (
                                            <tr>
                                                <td colSpan={onOpenClient ? 7 : 6} className="p-8 text-center text-slate-400">
                                                    Nessun partecipante trovato con i criteri di ricerca attuali.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredParticipants.map((p, idx) => {
                                                const isExpanded = expandedParticipantId === p.id;
                                                return (
                                                    <React.Fragment key={p.id || idx}>
                                                        <tr className={`hover:bg-slate-50/70 transition-colors ${isExpanded ? 'bg-indigo-50/20' : ''}`}>
                                                            {/* Index */}
                                                            <td className="p-4 text-center font-bold text-slate-400 text-xs">
                                                                {idx + 1}
                                                            </td>

                                                            {/* Participant Details */}
                                                            <td className="p-4">
                                                                <div className="flex flex-col">
                                                                    <span className="font-extrabold text-slate-900 text-sm">
                                                                        {p.name || 'Partecipante senza nome'}
                                                                    </span>
                                                                    {p.email && (
                                                                        <span className="text-[11px] text-slate-500 truncate max-w-xs font-medium">
                                                                            {p.email}
                                                                        </span>
                                                                    )}
                                                                    {p.managerName && (
                                                                        <span className="text-[10px] text-slate-400 font-semibold mt-0.5">
                                                                            Resp: {p.managerName}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Counselor Assegnato */}
                                                            <td className="p-4">
                                                                <div className="flex items-center gap-2.5">
                                                                    <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-200/80 flex items-center justify-center text-sm shadow-2xs shrink-0">
                                                                        🧑‍💼
                                                                    </div>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <span className="font-bold text-slate-800 text-xs truncate">
                                                                            {p.counselorDisplayName}
                                                                        </span>
                                                                        <span className="text-[10px] text-indigo-600 font-semibold">
                                                                            Counselor Titolare
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Numero Incontri */}
                                                            <td className="p-4">
                                                                <div className="space-y-1.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black bg-indigo-50 text-indigo-950 border border-indigo-200/80 shadow-2xs">
                                                                            <span>🛋️</span>
                                                                            <span><strong>{p.meetingCount}</strong> {p.meetingCount === 1 ? 'incontro' : 'incontri'}</span>
                                                                        </span>
                                                                        <span className="text-[10px] font-bold text-slate-400">
                                                                            / 8 previsti
                                                                        </span>
                                                                    </div>
                                                                    
                                                                    {/* Progress bar up to 8 meetings */}
                                                                    <div className="w-32 bg-slate-100 h-2 rounded-full overflow-hidden">
                                                                        <div 
                                                                            className={`h-full rounded-full transition-all duration-500 ${
                                                                                p.meetingCount >= 8 ? 'bg-emerald-500' : p.meetingCount > 0 ? 'bg-indigo-600' : 'bg-slate-200'
                                                                            }`}
                                                                            style={{ width: `${Math.min(100, (p.meetingCount / 8) * 100)}%` }}
                                                                        />
                                                                    </div>

                                                                    {p.meetingsList.length > 0 && p.meetingsList[0].date && (
                                                                        <span className="text-[10px] text-slate-400 block font-medium">
                                                                            Ultimo: {p.meetingsList[0].date}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Obiettivo di Percorso */}
                                                            <td className="p-4 max-w-md">
                                                                <div className="space-y-1.5">
                                                                    {p.goalInfo.initialGoal ? (
                                                                        <div className="p-3 bg-teal-50/70 border border-teal-200/80 rounded-2xl text-xs text-teal-950 font-medium leading-relaxed shadow-2xs">
                                                                            <span className="text-[10px] font-black uppercase text-teal-700 tracking-wider block mb-0.5 flex items-center gap-1">
                                                                                <span>🎯</span> Obiettivo Iniziale Rilevato:
                                                                            </span>
                                                                            "{p.goalInfo.initialGoal}"
                                                                        </div>
                                                                    ) : p.goalInfo.contractDescription ? (
                                                                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-800 font-medium leading-relaxed">
                                                                            <span className="text-[10px] font-black uppercase text-indigo-700 tracking-wider block mb-0.5">
                                                                                📋 Obiettivo da Contratto:
                                                                            </span>
                                                                            {p.goalInfo.contractDescription}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-2.5 bg-slate-50/80 rounded-xl border border-slate-200/60 text-[11px] text-slate-400 italic">
                                                                            In attesa del 1° incontro (Questionario IN)
                                                                        </div>
                                                                    )}

                                                                    {/* Selected areas of need */}
                                                                    {p.goalInfo.areas.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                                            {p.goalInfo.areas.map(a => (
                                                                                <span 
                                                                                    key={a.id} 
                                                                                    className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 rounded-lg text-[10px] font-semibold flex items-center gap-1"
                                                                                    title={a.label}
                                                                                >
                                                                                    <span>{a.icon}</span>
                                                                                    <span className="truncate max-w-[140px]">{a.label}</span>
                                                                                </span>
                                                                            ))}
                                                                            {p.goalInfo.otherArea && (
                                                                                <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-[10px] font-semibold">
                                                                                    Altro: {p.goalInfo.otherArea}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {p.goalInfo.organizationalNeed && p.goalInfo.organizationalNeed !== p.goalInfo.initialGoal && (
                                                                        <div className="text-[10px] text-slate-500 font-medium mt-0.5">
                                                                            🏢 <strong className="text-slate-600">Bisogno Organizzativo:</strong> {p.goalInfo.organizationalNeed}
                                                                        </div>
                                                                    )}

                                                                    {p.goalInfo.goalAchievement && (
                                                                        <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-purple-100 text-purple-800 border border-purple-200/80 mt-1">
                                                                            <span>🏆</span> Esito Obiettivo Finale: {p.goalInfo.goalAchievement} / 5
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Stato Questionari */}
                                                            <td className="p-4 text-center">
                                                                <div className="flex flex-col items-center gap-1.5">
                                                                    {p.goalInfo.hasIn ? (
                                                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-teal-100 text-teal-800 border border-teal-200/80 inline-flex items-center gap-1">
                                                                            <span>✅</span> IN Compilato
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                                                                            <span>⏳</span> IN In Attesa
                                                                        </span>
                                                                    )}

                                                                    {p.goalInfo.hasOut ? (
                                                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200/80 inline-flex items-center gap-1">
                                                                            <span>🏁</span> OUT Chiuso
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold text-slate-400">
                                                                            {p.meetingCount >= 8 ? 'Pronto per OUT' : 'In corso (OUT a 8°)'}
                                                                        </span>
                                                                    )}

                                                                    {/* Toggle meeting history */}
                                                                    {p.meetingsList.length > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedParticipantId(isExpanded ? null : p.id)}
                                                                            className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold underline mt-1"
                                                                        >
                                                                            {isExpanded ? 'Chiudi sedute ▲' : `Dettaglio sedute (${p.meetingsList.length}) ▼`}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Azioni */}
                                                            {onOpenClient && (
                                                                <td className="p-4 text-right">
                                                                    <button
                                                                        onClick={() => onOpenClient(p)}
                                                                        className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white border border-indigo-200 text-indigo-700 rounded-xl text-xs font-bold transition flex items-center gap-1 ml-auto shadow-2xs"
                                                                        title="Apri scheda dettagliata cliente"
                                                                    >
                                                                        <span>Scheda</span>
                                                                        <span>→</span>
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>

                                                        {/* Expanded row with meeting history */}
                                                        {isExpanded && (
                                                            <tr className="bg-indigo-50/20 border-b border-indigo-100">
                                                                <td colSpan={onOpenClient ? 7 : 6} className="p-4 pl-12">
                                                                    <div className="bg-white p-4 rounded-2xl border border-indigo-100/80 space-y-3">
                                                                        <div className="flex items-center justify-between">
                                                                            <h5 className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                                                                                <span>🛋️</span> Storico Sedute Registrate per {p.name || 'Cliente'} ({p.meetingsList.length})
                                                                            </h5>
                                                                            <span className="text-[10px] font-bold text-slate-400">
                                                                                Counselor: {p.counselorDisplayName}
                                                                            </span>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                                            {p.meetingsList.map((m, mIdx) => (
                                                                                <div key={m.id || mIdx} className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 text-xs">
                                                                                    <div className="flex items-center justify-between font-bold text-slate-700">
                                                                                        <span>Seduta #{p.meetingsList.length - mIdx}</span>
                                                                                        <span className="text-[10px] text-indigo-600 font-semibold">{m.date || 'Data non specificata'}</span>
                                                                                    </div>
                                                                                    {m.notes && (
                                                                                        <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                                                                                            {m.notes}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                        </section>
                    )}

                    {/* ========================================================================= */}
                    {/* SEZIONE 2: SEZIONE A (BISOGNI INIZIALI DETTAGLIATI) */}
                    {/* ========================================================================= */}
                    {(viewMode === 'sequence' || activeTab === 'sectionA') && (
                        <section id="sec-sectionA" className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6 scroll-mt-24">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-[10px] font-black uppercase tracking-wider block w-fit mb-2">
                                        🎯 SEZIONE 2 — BISOGNI INIZIALI (SEZIONE A)
                                    </span>
                                    <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                        Distribuzione Completa dei Bisogni di Partenza
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Misurazione esatta di quanti clienti hanno selezionato ciascuna area di bisogno sui {sectionAStats.totalIn} questionari IN somministrati.
                                    </p>
                                </div>
                                <div className="px-4 py-2 bg-teal-50 rounded-2xl border border-teal-100 text-right shrink-0">
                                    <span className="text-[10px] font-bold text-teal-600 uppercase block">Questionari IN Rilevati</span>
                                    <span className="text-lg font-black text-teal-900">{sectionAStats.totalIn} / {clients.length}</span>
                                </div>
                            </div>

                            {/* Table of all 9 items */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            <th className="p-3.5 rounded-l-xl">Area di Bisogno</th>
                                            <th className="p-3.5 text-center">N° Risposte</th>
                                            <th className="p-3.5 text-center">Percentuale (%)</th>
                                            <th className="p-3.5 rounded-r-xl w-1/3">Distribuzione Visiva</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-xs">
                                        {sectionAStats.items.map((item, idx) => (
                                            <tr key={item.id} className="hover:bg-slate-50/60 transition">
                                                <td className="p-3.5 font-bold text-slate-800 flex items-center gap-2">
                                                    <span className="text-base">{item.icon}</span>
                                                    <span>{item.label}</span>
                                                </td>
                                                <td className="p-3.5 text-center font-black text-teal-800 text-sm">
                                                    {item.count}
                                                </td>
                                                <td className="p-3.5 text-center font-bold text-slate-700">
                                                    {item.percentage}%
                                                </td>
                                                <td className="p-3.5">
                                                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                                                        <div 
                                                            className="bg-teal-600 h-full rounded-full" 
                                                            style={{ width: `${item.percentage}%` }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Altro Specificato */}
                            {sectionAStats.otherExamples.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-slate-100">
                                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                                        Dettaglio Voci "Altro" Specificato ({sectionAStats.otherExamples.length})
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {sectionAStats.otherExamples.map((ex, i) => (
                                            <div key={i} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-700">
                                                <span className="font-bold text-teal-800">{ex.client}:</span> "{ex.text}"
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* ========================================================================= */}
                    {/* SEZIONE 3: SEZIONE B (DELTA IN vs OUT SU 10 ITEM) */}
                    {/* ========================================================================= */}
                    {(viewMode === 'sequence' || activeTab === 'sectionB') && (
                        <section id="sec-sectionB" className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6 scroll-mt-24">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-[10px] font-black uppercase tracking-wider block w-fit mb-2">
                                        📈 SEZIONE 3 — DELTA PRIMA / DOPO (SEZIONE B)
                                    </span>
                                    <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                        Confronto Prima / Dopo: I 10 Indicatori di Autopercezione
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Misura della crescita media tra il 1° colloquio (IN) e l'8° colloquio (OUT) su scala 1–5.
                                    </p>
                                </div>

                                <div className="flex items-center gap-4 text-xs font-bold shrink-0">
                                    <span className="flex items-center gap-1.5 text-teal-700">
                                        <span className="w-3 h-3 rounded-full bg-teal-600 inline-block"></span> Media IN
                                    </span>
                                    <span className="flex items-center gap-1.5 text-emerald-700">
                                        <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block"></span> Media OUT
                                    </span>
                                </div>
                            </div>

                            {/* Impatto Globale Cards (Media IN, Media OUT, Delta & Restituzione Ente) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="p-4 bg-teal-50 rounded-2xl border border-teal-100 text-center">
                                    <span className="text-[10px] font-bold text-teal-600 uppercase block tracking-wider">Media IN (Inizio)</span>
                                    <span className="text-3xl font-black text-teal-800 my-1 block">{sectionBStats.globalAvgIn || 'N/D'}</span>
                                    <span className="text-[11px] text-teal-700 font-medium">1° colloquio (avvio)</span>
                                </div>
                                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 text-center">
                                    <span className="text-[10px] font-bold text-emerald-600 uppercase block tracking-wider">Media OUT (Fine)</span>
                                    <span className="text-3xl font-black text-emerald-800 my-1 block">{sectionBStats.globalAvgOut || 'N/D'}</span>
                                    <span className="text-[11px] text-emerald-700 font-medium">8° colloquio (chiusura)</span>
                                </div>
                                <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 text-center">
                                    <span className="text-[10px] font-bold text-indigo-600 uppercase block tracking-wider">Delta di Crescita</span>
                                    <span className="text-3xl font-black text-indigo-800 my-1 block">
                                        {sectionBStats.globalDelta ? `+${sectionBStats.globalDelta}` : 'N/D'}
                                    </span>
                                    <span className="text-[11px] text-indigo-700 font-medium">Variazione complessiva</span>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-700 flex items-center">
                                    <div>
                                        💡 <strong className="text-slate-900">Restituzione Ente:</strong> I partecipanti registrano una crescita media costante su tutte le 10 dimensioni di autopercezione e consapevolezza lavorativa.
                                    </div>
                                </div>
                            </div>

                            {/* 10 Items Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            <th className="p-3.5 rounded-l-xl w-2/5">Indicatore / Domanda</th>
                                            <th className="p-3.5 text-center">Media IN</th>
                                            <th className="p-3.5 text-center">Media OUT</th>
                                            <th className="p-3.5 text-center">Delta (pt)</th>
                                            <th className="p-3.5 text-center rounded-r-xl">Crescita (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-xs">
                                        {sectionBStats.items.map((item, idx) => {
                                            const hasGrowth = item.delta && item.delta > 0;
                                            return (
                                                <tr key={item.id} className="hover:bg-slate-50/60 transition">
                                                    <td className="p-3.5">
                                                        <span className="font-bold text-slate-800 block">{item.text}</span>
                                                        <span className="text-[10px] text-slate-400 font-semibold">{item.shortLabel}</span>
                                                    </td>
                                                    <td className="p-3.5 text-center font-bold text-teal-800">
                                                        {item.avgIn ? `${item.avgIn}` : '-'}
                                                    </td>
                                                    <td className="p-3.5 text-center font-bold text-emerald-800">
                                                        {item.avgOut ? `${item.avgOut}` : '-'}
                                                    </td>
                                                    <td className="p-3.5 text-center font-black">
                                                        {item.delta !== null ? (
                                                            <span className={`px-2.5 py-1 rounded-full text-xs ${
                                                                hasGrowth ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                                                            }`}>
                                                                {item.delta > 0 ? `+${item.delta}` : item.delta}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="p-3.5 text-center font-black">
                                                        {item.percentChange !== null ? (
                                                            <span className={`text-xs ${
                                                                hasGrowth ? 'text-emerald-700' : 'text-slate-600'
                                                            }`}>
                                                                {item.percentChange > 0 ? `+${item.percentChange}%` : `${item.percentChange}%`}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* ========================================================================= */}
                    {/* SEZIONE 4: SEZIONE C (VALUTAZIONE ED ESITI) */}
                    {/* ========================================================================= */}
                    {(viewMode === 'sequence' || activeTab === 'sectionC') && (
                        <section id="sec-sectionC" className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6 scroll-mt-24">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-[10px] font-black uppercase tracking-wider block w-fit mb-2">
                                        🏆 SEZIONE 4 — VALUTAZIONE DEGLI ESITI (SEZIONE C)
                                    </span>
                                    <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                        Valutazione degli Esiti, Obiettivi & Testimonianze
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Misurazione qualitativa e quantitativa raccolta al termine del percorso (8° colloquio).
                                    </p>
                                </div>
                                <div className="px-4 py-2 bg-purple-50 rounded-2xl border border-purple-100 text-right shrink-0">
                                    <span className="text-[10px] font-bold text-purple-600 uppercase block">Questionari OUT Compilati</span>
                                    <span className="text-lg font-black text-purple-900">{sectionCStats.totalOut} / {clients.length}</span>
                                </div>
                            </div>
                            
                            {/* NPS & Goal Achievement Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                {/* Goal Achievement */}
                                <div className="bg-slate-50/80 p-6 rounded-3xl border border-slate-200 shadow-2xs">
                                    <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">Domanda 1</span>
                                    <h4 className="font-bold text-slate-800 text-sm mt-0.5 mb-2">
                                        Raggiungimento dell'obiettivo iniziale
                                    </h4>
                                    <p className="text-2xl font-black text-purple-900 mb-4">
                                        {sectionCStats.avgGoal ? `${sectionCStats.avgGoal} / 5.0` : 'Nessuna risposta'}
                                    </p>

                                    <div className="space-y-2">
                                        {LIKERT_OPTIONS.map(opt => {
                                            const count = sectionCStats.goalDistribution[opt.value] || 0;
                                            const pct = sectionCStats.totalOut > 0 ? ((count / sectionCStats.totalOut) * 100).toFixed(0) : 0;
                                            return (
                                                <div key={opt.value} className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-600">{opt.label}</span>
                                                    <span className="font-bold text-slate-800">{count} risposte ({pct}%)</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* NPS Recommendation */}
                                <div className="bg-slate-50/80 p-6 rounded-3xl border border-slate-200 shadow-2xs">
                                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block">Domanda 5</span>
                                    <h4 className="font-bold text-slate-800 text-sm mt-0.5 mb-2">
                                        Consiglieresti People Care a un collega?
                                    </h4>
                                    <p className="text-2xl font-black text-amber-900 mb-4">
                                        {sectionCStats.avgNps ? `${sectionCStats.avgNps} / 5.0` : 'Nessuna risposta'}
                                    </p>

                                    <div className="space-y-2">
                                        {LIKERT_OPTIONS.map(opt => {
                                            const count = sectionCStats.npsDistribution[opt.value] || 0;
                                            const pct = sectionCStats.totalOut > 0 ? ((count / sectionCStats.totalOut) * 100).toFixed(0) : 0;
                                            return (
                                                <div key={opt.value} className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-600">{opt.label}</span>
                                                    <span className="font-bold text-slate-800">{count} risposte ({pct}%)</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Qualitative Feedback Cards */}
                            {sectionCStats.testimonials.length > 0 && (
                                <div className="pt-4 border-t border-slate-100 space-y-4">
                                    <h4 className="text-base font-bold text-slate-800">
                                        Testimonianze ed Esiti Qualitativi dei Partecipanti
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {sectionCStats.testimonials.map((t, idx) => (
                                            <div key={idx} className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2.5 text-xs">
                                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                                    <span className="font-bold text-slate-900">{t.client}</span>
                                                    {t.manager && <span className="text-[10px] text-slate-400">Resp: {t.manager}</span>}
                                                </div>
                                                {t.whatChanged && (
                                                    <p><strong className="text-slate-700">Cosa è cambiato:</strong> {t.whatChanged}</p>
                                                )}
                                                {t.mostUseful && (
                                                    <p><strong className="text-slate-700">Cosa è stato utile:</strong> {t.mostUseful}</p>
                                                )}
                                                {t.differentApproach && (
                                                    <p><strong className="text-slate-700">Diverso approccio:</strong> {t.differentApproach}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </section>
                    )}

                    {/* ========================================================================= */}
                    {/* SEZIONE 5: MATRICE COMPETENZE (LEGENDA COLORI) */}
                    {/* ========================================================================= */}
                    {(viewMode === 'sequence' || activeTab === 'competencies') && (
                        <section id="sec-competencies" className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6 scroll-mt-24">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-[10px] font-black uppercase tracking-wider block w-fit mb-2">
                                        ⭐ SEZIONE 5 — MATRICE COMPETENZE TRASVERSALI
                                    </span>
                                    <h3 className="text-xl font-black text-slate-800 tracking-tight">
                                        Matrice Competenze Trasversali & Fasce di Presidio
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Classificazione dei partecipanti BuenaVista nelle 5 fasce della Legenda Competenze.
                                    </p>
                                </div>

                                {competenciesStats.avgScore && (
                                    <div className="px-4 py-2 bg-slate-100 rounded-2xl text-right shrink-0">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase block">Indice Medio Trasversale</span>
                                        <span className="text-lg font-black text-slate-900">{competenciesStats.avgScore} / 5.0</span>
                                    </div>
                                )}
                            </div>

                            {/* Legend Band Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                {competenciesStats.bands.map(band => (
                                    <div 
                                        key={band.id}
                                        className={`p-4 rounded-2xl border ${band.bg} ${band.border} flex flex-col justify-between`}
                                    >
                                        <div>
                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                                                {band.min === 3.25 ? '≥ 3.25' : band.min === 0 ? '< 2.50' : `${band.min.toFixed(2)} - ${band.max.toFixed(2)}`}
                                            </span>
                                            <h4 className={`font-black text-sm mt-0.5 ${band.text}`}>
                                                {band.label}
                                            </h4>
                                        </div>
                                        <div className="mt-4 pt-2 border-t border-slate-200/50 flex justify-between items-baseline">
                                            <span className="text-2xl font-black text-slate-900">{band.count}</span>
                                            <span className="text-xs font-bold text-slate-600">{band.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Client List with Competencies */}
                            <div className="mt-6 pt-6 border-t border-slate-100">
                                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                                    Elenco Partecipanti con Indice Competenze ({competenciesStats.totalWithScore})
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-500 uppercase">
                                                <th className="p-3">Partecipante</th>
                                                <th className="p-3">Resp. Gerarchico</th>
                                                <th className="p-3 text-center">Indice Score</th>
                                                <th className="p-3">Fascia Legenda</th>
                                                <th className="p-3">Bisogno Organizzativo</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {clients.map(c => {
                                                const band = getCompetencyBand(c.competencyScore);
                                                return (
                                                    <tr key={c.id} className="hover:bg-slate-50/60">
                                                        <td className="p-3 font-bold text-slate-800">{c.name || c.email}</td>
                                                        <td className="p-3 text-slate-600">{c.managerName || '-'}</td>
                                                        <td className="p-3 text-center font-black text-slate-900">
                                                            {c.competencyScore ? Number(c.competencyScore).toFixed(2) : '-'}
                                                        </td>
                                                        <td className="p-3">
                                                            {band ? (
                                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border ${band.badgeClass}`}>
                                                                    {band.label}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-400 italic">Non impostato</span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-slate-600 max-w-xs truncate">
                                                            {c.organizationalNeed || '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}
                </>
            )}

        </div>
    );
};

export default PeopleCareAnalyticsView;
